import {Concept} from 'types';
import type {
  AnyValidation,
  Condition,
  Part,
  PartSchema,
  TypedSchema,
  TypedSchemaModifierMap,
  ValuePartModifier,
} from 'schemaDefinition/types';
import {AlwaysIncludedSeparator, Separator} from 'schemaDefinition/types';
import type {
  ManifestationImportProps,
  ManifestationV4,
} from 'services/metadata/types';
import {translate} from 'localization';
import type {MetadataModifierKey} from '../types';
import {StatusModifierKey, WorkBook, WorkMerchandise} from '../types';
import {CodeListRef} from '../references';
import {singleCodePrefixRegex} from './functions';
import {regexStartsWithNoneOf} from './functions/regexStartsWithNoneOf';
import {validateLinkedImprintId} from './partAgentsValidations';

type ManifestationSchema = TypedSchema<
  ManifestationV4,
  ManifestationImportProps
>;

type ManifestationCondition<
  TName extends keyof ManifestationV4 = keyof ManifestationV4,
> = Condition<
  ManifestationV4,
  ManifestationV4,
  ManifestationV4[TName],
  ManifestationImportProps
>;

type ManifestationValidation<
  TName extends keyof ManifestationV4 = keyof ManifestationV4,
> = AnyValidation<
  ManifestationV4,
  ManifestationV4,
  ManifestationV4[TName],
  ManifestationImportProps
>;

type ManifestationPart = Part<
  ManifestationV4,
  ManifestationV4,
  ManifestationImportProps
>;

type ManifestationPartSchema = PartSchema<
  ManifestationV4,
  ManifestationV4,
  ManifestationImportProps
>;

/**
 * Manifestation schema
 */

const pageCountRegex =
  '^(([1-9][0-9]*)|((?=[MDCLXVI])M*(C[MD]|D?C*)(X[CL]|L?X*)(I[XV]|V?I*)))(,\\s*(([1-9][0-9]*)|((?=[MDCLXVI])M*(C[MD]|D?C*)(X[CL]|L?X*)(I[XV]|V?I*))))*$';
// Plain digits (e.g. "25000") or space-separated thousands (e.g. "25 000")
const scaleRegex = '^[1-9][0-9]*$|^[1-9][0-9]{0,2}( [0-9]{3})+$';
const runtimeRegex = [
  // hh:mm:ss
  '^[0-9]{1,3}:[0-5][0-9]:[0-5][0-9]$',
  // // hh:mm / mm:ss
  // '^[0-9]{1,2}:[0-5][0-9]$',
  // // mm / ss
  // '^[0-5]?[0-9]$',
];

// Groups of product forms with specific properties

const matchCodesRegex = (codes: string[]) =>
  `^${codes.map(c => `(${c})`).join('|')}$`;

const pagesColorAndScaleForms = [
  CodeListRef.PRODUCT_FORM['Innbundet'],
  CodeListRef.PRODUCT_FORM['Heftet'],
  CodeListRef.PRODUCT_FORM['Fleksibind'],
  CodeListRef.PRODUCT_FORM['Spiralbundet'],
  CodeListRef.PRODUCT_FORM['Pappbok'],
  CodeListRef.PRODUCT_FORM['Løse ark'],
  CodeListRef.PRODUCT_FORM['Badebok'],
  CodeListRef.PRODUCT_FORM['Tekstilbok'],
];

const scaleForms = [
  // Starting with B
  'B.',
  // Or:
  CodeListRef.PRODUCT_FORM['Kart, falset'],
  CodeListRef.PRODUCT_FORM['Kart, rullet'],
];

// All product forms with any code starting with 'S'
const colourForms = [
  'S.',
  CodeListRef.PRODUCT_FORM['Dagbok'],
  CodeListRef.PRODUCT_FORM['Leke'],
  CodeListRef.PRODUCT_FORM['Pakke (i eske)'],
  CodeListRef.PRODUCT_FORM['Myk leke'],
  CodeListRef.PRODUCT_FORM['Notatbok'],
  CodeListRef.PRODUCT_FORM['Varer, uspesifisert'],
];

const fileFormatVersionForms = [
  CodeListRef.PRODUCT_FORM_DETAIL['EPUB'],
  CodeListRef.PRODUCT_FORM_DETAIL['OEB'],
];

const pagesForms = [
  CodeListRef.PRODUCT_FORM['Lysark'],
  CodeListRef.PRODUCT_FORM['Film eller lysark'],
  CodeListRef.PRODUCT_FORM.ED,
];

const noForms = [
  CodeListRef.PRODUCT_FORM.EB,
  CodeListRef.PRODUCT_FORM['Mappe eller omslag'],
  CodeListRef.PRODUCT_FORM['Plansje'],
  CodeListRef.PRODUCT_FORM['Diskett'],
  CodeListRef.PRODUCT_FORM['Andre trykte artikler'],
  CodeListRef.PRODUCT_FORM['Dagbok'],
  CodeListRef.PRODUCT_FORM['Postkort'],
  CodeListRef.PRODUCT_FORM['Diverse trykk'],
  CodeListRef.PRODUCT_FORM['Kalender'],
  CodeListRef.PRODUCT_FORM['Plakat'],
  CodeListRef.PRODUCT_FORM['Notatbok'],
  CodeListRef.PRODUCT_FORM['Diskeske- fylt'],
  CodeListRef.PRODUCT_FORM['Globus'],
  CodeListRef.PRODUCT_FORM['Andre klær'],
  CodeListRef.PRODUCT_FORM['Lydbokspiller'],
  CodeListRef.PRODUCT_FORM['Lesebrett'],
  CodeListRef.PRODUCT_FORM['Leke'],
  CodeListRef.PRODUCT_FORM['Puslespill'],
  CodeListRef.PRODUCT_FORM['Spill'],
  CodeListRef.PRODUCT_FORM['Andre varer'],
  CodeListRef.PRODUCT_FORM['Myk leke'],
  CodeListRef.PRODUCT_FORM['Servise'],
  CodeListRef.PRODUCT_FORM['Paraply'],
  CodeListRef.PRODUCT_FORM['Handlenett'],
  CodeListRef.PRODUCT_FORM['Varer, uspesifisert'],
  CodeListRef.PRODUCT_FORM['Pakke (i eske)'],
];

const runtimeForms = [
  CodeListRef.PRODUCT_FORM['Lydfil'],
  CodeListRef.PRODUCT_FORM['CD (lyd)'],
  CodeListRef.PRODUCT_FORM['Lydbånd'],
  CodeListRef.PRODUCT_FORM['Lydkassett'],
  CodeListRef.PRODUCT_FORM['Diskett'],
  CodeListRef.PRODUCT_FORM['Blu-ray'],
  CodeListRef.PRODUCT_FORM['DVD video'],
  CodeListRef.PRODUCT_FORM['Film'],
  CodeListRef.PRODUCT_FORM['MP3-spiller med innhold'],
  CodeListRef.PRODUCT_FORM['Video'],
  CodeListRef.PRODUCT_FORM['Annet digitalt format (på fysisk bærer)'],
];

const statementOfResponsibilityForms = ['^B', CodeListRef.PRODUCT_FORM['ED']];
const isAutoGenerateStatementOfResponsibility: ManifestationCondition<'autoGenerateStatementOfResponsibility'> =
  {
    arg: {$ref: ':autoGenerateStatementOfResponsibility'},
  };

const showStatementOfResponsibilityForm: ManifestationCondition<'productForm'> =
  {
    arg: {$ref: ':productForm'},
    regex: statementOfResponsibilityForms,
    default: false,
  };

const isBook: ManifestationCondition = {
  $eq: [{$ref: '^work.type'}, CodeListRef.WORK_TYPE.Bok],
};

const isMerch: ManifestationCondition = {
  $eq: [{$ref: '^work.type'}, CodeListRef.WORK_TYPE.Vare],
};

const isIntellectualLevel_children: ManifestationCondition = {
  $includes: {
    arg1: {$ref: '^work.intellectualLevel'},
    arg2: [
      CodeListRef.INTELLECTUAL_LEVEL['0-3'],
      CodeListRef.INTELLECTUAL_LEVEL['3-6'],
      CodeListRef.INTELLECTUAL_LEVEL['6-9'],
      CodeListRef.INTELLECTUAL_LEVEL['9-12'],
      CodeListRef.INTELLECTUAL_LEVEL['12-16'],
      CodeListRef.INTELLECTUAL_LEVEL['16-20'],
    ],
  },
  default: false,
};

const isProductGroup_children: ManifestationCondition<'productGroup'> = {
  $or: [
    {
      regex: singleCodePrefixRegex(CodeListRef.PRODUCT_GROUP.Prefix_Barnebøker),
      arg: {$ref: ':productGroup'},
      default: false,
    },
    {
      regex: singleCodePrefixRegex(
        CodeListRef.PRODUCT_GROUP.Prefix_Ungdomsbøker,
      ),
      arg: {$ref: ':productGroup'},
      default: false,
    },
  ],
};

const isMapCB: ManifestationCondition<'productForm'> = {
  regex: CodeListRef.PRODUCT_FORM['Kart, falset'],
  arg: {$ref: '/productForm'},
  default: true,
};
const isMapCC: ManifestationCondition<'productForm'> = {
  regex: CodeListRef.PRODUCT_FORM['Kart, rullet'],
  arg: {$ref: '/productForm'},
  default: true,
};

const isLydfil: ManifestationCondition<'productForm'> = {
  $eq: [{$ref: '/productForm'}, CodeListRef.PRODUCT_FORM.Lydfil],
};
const isEbook: ManifestationCondition<'productForm'> = {
  $eq: [{$ref: '/productForm'}, CodeListRef.PRODUCT_FORM.Ebok],
};

const isOppositeOfDigitalFormat: ManifestationCondition<'productForm'> = {
  regex: regexStartsWithNoneOf(['A', 'C', 'E']),
  arg: {$ref: '/productForm'},
  default: false,
};

/**
 * Product form details will be empty/hidden if this condition is not met.
 */
const merchProductFormWithDetails: ManifestationCondition<'productForm'> = {
  $or: [
    {
      $eq: [{$ref: '^work.type'}, CodeListRef.WORK_TYPE.Bok],
    },
    // If vare, must have one of these productForms to show productFormDetail:
    {
      $includes: {
        arg1: {$ref: '/productForm'},
        arg2: [
          CodeListRef.PRODUCT_FORM.Dagbok,
          CodeListRef.PRODUCT_FORM.Notatbok,
          CodeListRef.PRODUCT_FORM.Kalender,
        ],
      },
      default: false,
    },
  ],
};

const validate_productGroup_child_only_whenIntellectualChild: ManifestationValidation =
  // Product group child/youth requires intellectual level child
  {
    $or: [isIntellectualLevel_children, {$not: isProductGroup_children}],
    level: 'warning',
    messageKey: 'productGroup.child.requires.intellectualLevel.child',
  };

const imprintPlaceholderString = translate('form.errors.placeNotIdentified');
const validateLinkedImprintPlacePlaceholderString: ManifestationValidation<'imprints'> =
  {
    level: 'warning',
    messageKey: 'placeNotIdentified',
    $not: {
      $eq: [{$ref: '.link.place'}, imprintPlaceholderString],
    },
  };

const validateLinkedImprintPlaceEmpty: ManifestationValidation<'imprints'> = {
  level: 'error',
  arg: {$ref: '.link.place'},
  messageKey: 'imprint.no.place',
};

const pageParts: ManifestationPart[] = [
  {
    type: 'text',
    name: 'pageCount',
    validation: {regex: pageCountRegex, default: true},
  },
  {
    type: 'bool',
    name: 'unnumberedPages',
  },
];

const runtimePart: ManifestationPart = {
  type: 'text',
  name: 'runtimeSeconds',
  validation: {regex: runtimeRegex, default: true},
};
const scalePart: ManifestationPart = {
  type: 'text',
  name: 'scale',
  key: 'scale',
  prefix: '1:',
  thousandSeparator: true,
  cardinality: 'multiple',
  validation: {regex: scaleRegex, default: true},
};
const colourPart: ManifestationPart = {
  type: 'codelist',
  name: 'colour',
  codelistId: 'COLOUR',
};

type WithMainTitles = {
  mainTitles: Required<ManifestationV4>['mainTitles'] | null;
};

export const mainTitlesPart: PartSchema<
  WithMainTitles,
  WithMainTitles,
  ManifestationImportProps
> = {
  type: 'schema',
  name: 'mainTitles',
  cardinality: 'multiple',
  required: true,
  compare: 'subValues',
  parts: [
    {
      type: 'text',
      name: 'value',
      key: 'mainTitlesValue',
      required: true,
    },
    {
      type: 'schema',
      name: 'subTitles',
      cardinality: 'multiple',
      parts: [
        {
          type: 'text',
          name: 'value',
          key: 'subTitlesValue',
          required: true,
        },
        {
          name: 'parallelTitles',
          key: 'parallelSubTitles',
          type: 'text',
          cardinality: 'multiple',
        },
      ],
    },
    {
      name: 'parallelTitles',
      type: 'text',
      cardinality: 'multiple',
    },
  ],
};

const productOwnerSchema: ManifestationPartSchema = {
  type: 'schema',
  name: 'productOwners',
  key: 'productOwners',
  parts: [
    {
      type: 'text',
      name: 'code',
      key: 'productOwner.code',
      readonly: true,
    },
    {
      type: 'text',
      name: 'name',
      key: 'productOwner.name',
      readonly: true,
    },
  ],
  readonly: true,
  cardinality: 'multiple',
};

const productSchema: ManifestationPartSchema = {
  type: 'schema',
  name: 'products',
  key: 'products',
  parts: [
    {
      type: 'text',
      name: 'owner',
      key: 'product.owner.code',
      readonly: true,
    },
    {
      type: 'text',
      name: 'distributor',
      key: 'product.distributor.code',
      readonly: true,
    },
  ],
  readonly: true,
  cardinality: 'multiple',
};

const isbnPart: ManifestationPart = {
  type: 'text',
  name: 'isbn',
  readonly: true,
  actions: ['copy'],
};

const ismnPart: ManifestationPart = {
  type: 'text',
  name: 'ismn',
  readonly: true,
  actions: ['copy'],
};

const eanPart: ManifestationPart = {
  type: 'text',
  name: 'ean',
  readonly: true,
  validation: [
    {regex: '^[0-9]{13}$', default: true},
    {
      $or: [
        {arg: {$ref: '/ean'}}, // ean (this) is set, or
        {arg: {$ref: '/isbn'}}, // isbn is set
      ],
    },
  ],
};

const contentReceivedDatePart: ManifestationPart = {
  type: 'date',
  name: 'contentReceivedDate',
  readonly: true,
};

const hasIsbn: ManifestationCondition<'isbn'> = {
  arg: {$ref: '/isbn'},
};
const hasIsmn: ManifestationCondition<'ismn'> = {
  arg: {$ref: '/ismn'},
};
const hasBothIsbnAndIsbn: ManifestationCondition[] = [hasIsbn, hasIsmn];

const isTextOrTextIllustrated: ManifestationCondition = {
  arg: {$ref: '^expression.expressionFormat'},
  regex: [
    CodeListRef.EXPRESSION_FORMAT.TEXT,
    CodeListRef.EXPRESSION_FORMAT.ILLUSTRATED_TEXT,
  ],
  default: false,
};

const publishedYearNotPrintedInBookPartExpand: ManifestationPart = {
  type: 'expand',
  role: 'showMissingPublishedYearWhenText',
  when: [
    {
      condition: isTextOrTextIllustrated,
      parts: [{type: 'bool', name: 'publishedYearNotPrintedInBook'}],
    },
  ],
};

const editionPart: ManifestationPart = {
  type: 'int',
  name: 'edition',
  validation: {
    min: 1,
    max: 99999,
    messageKey: 'invalid_number',
  },
};
export const publishedYearPart: ManifestationPart = {
  type: 'year',
  name: 'publishedYear',
  required: true,
};
const editionAndPublishedYearParts: ManifestationPart[] = [
  editionPart,
  publishedYearPart,
];

export const manifestationDataSchema: ManifestationSchema = {
  name: 'manifestation',
  key: 'manifestation',
  parts: [
    [
      {
        type: 'expand',
        role: 'showAndLayoutIsbnIsmnAndEditionAndPublishedYear',
        when: [
          {
            // Both isbn and ismn is set, show both, with edition and published year on separate line
            condition: hasBothIsbnAndIsbn,
            parts: [[isbnPart, ismnPart], editionAndPublishedYearParts],
          },
          {
            // When ismn is set, show ismn and edition and published year on same line
            condition: hasIsmn,
            parts: [[ismnPart, ...editionAndPublishedYearParts]],
          },
        ],
        // Otherwise show isbn and edition and published year on same line
        default: [[isbnPart, ...editionAndPublishedYearParts]],
      },
      publishedYearNotPrintedInBookPartExpand,
    ],
    [eanPart, contentReceivedDatePart],
    {
      type: 'text',
      name: 'wrongIsbn',
      cardinality: 'multiple',
    },
    productOwnerSchema,
    productSchema,
    {
      type: 'linkedAgent',
      name: 'imprints',
      cardinality: 'multiple',
      entityRole: 'publisher',
      entitySubtypes: [Concept.publisher],
      roleCodelistId: 'manifestation_role_type',
      validation: [validateLinkedImprintId],
      disableDirectEdit: true,
    },
    Separator('line', 'line'),
    mainTitlesPart,
    {
      type: 'schema',
      name: 'otherTitles',
      parts: [
        [
          {
            type: 'text',
            name: 'value',
            key: 'otherTitle.value',
            required: true,
          },
          {
            type: 'codelist',
            name: 'type',
            key: 'otherTitle.type',
            codelistId: 'TITLE_TYPE',
            required: true,
          },
        ],
      ],
      cardinality: 'multiple',
    },
    {
      type: 'bool',
      name: 'autoGenerateStatementOfResponsibility',
      key: 'autoGenerateStatementOfResponsibility',
      default: true,
    },
    {
      type: 'expand',
      role: 'showStatementOfResponsibilityIfCorrentProductForm',
      when: [
        {
          condition: [
            {
              $not: isAutoGenerateStatementOfResponsibility,
            },
            showStatementOfResponsibilityForm,
          ],
          parts: [
            Separator('line', 'line'),
            {
              type: 'textarea',
              name: 'statementOfResponsibility',
              key: 'statementOfResponsibility',
            },
          ],
        },
      ],
    },
    Separator('line', 'line'),
    {
      type: 'schema',
      name: 'notes',
      key: 'notes',
      parts: [
        {
          type: 'textarea',
          name: 'generalNote',
          key: 'notes.generalNote',
          cardinality: 'multiple',
        },
        {
          type: 'textarea',
          name: 'editionNote',
          key: 'notes.editionNote',
          cardinality: 'multiple',
        },
        {
          type: 'codelist',
          name: 'editionType',
          key: 'notes.editionType',
          codelistId: 'EDITION_TYPE',
          cardinality: 'multiple',
        },
        {
          type: 'expand',
          role: 'showAttachmentIfBook',
          when: [
            {
              condition: isBook,
              parts: [
                {
                  type: 'text',
                  name: 'attachment',
                  key: 'notes.attachment',
                  cardinality: 'multiple',
                },
              ],
            },
          ],
        },
      ],
    },
    Separator('line', 'line'),
    {
      type: 'linkedLiterary',
      name: 'links',
      key: 'publisherSeries',
      cardinality: 'multiple',
      roleCodelistId: 'manifestation.linkedRole',
      linkRole: 'partOf.publisherSeries',
    },
    Separator('line', 'line'),
    {
      type: 'expand',
      role: 'descriptionContent',
      when: [
        {
          condition: {arg: {$ref: ':descriptionContent'}},
          parts: [
            {
              type: 'html',
              name: 'descriptionContent',
              key: 'descriptionContent',
              readonly: true,
            },
            AlwaysIncludedSeparator('line', 'line'),
          ],
        },
      ],
    },
    {
      type: 'codelist',
      name: 'productForm',
      codelistId: 'PRODUCT_FORM',
      validation: {
        arg: {$ref: '^work.type'},
      },
    },
    {
      type: 'expand',
      role: 'productFormHasDetails',
      when: [
        {
          condition: merchProductFormWithDetails,
          parts: [
            {
              type: 'codelist',
              name: 'productFormDetail',
              cardinality: 'multiple',
              codelistId: 'PRODUCT_FORM_DETAIL',
              // Validation for productformdetail is added by schema modifier (see getManifestationBasedModifier)
            },
          ],
        },
      ],
    },
    {
      type: 'expand',
      role: 'fileFormatVersionRole',
      when: [
        {
          condition: {
            arg: {$ref: '/productFormDetail'},
            regex: fileFormatVersionForms,
            default: false,
          },
          parts: [
            {
              type: 'codelist',
              name: 'fileFormatVersion',
              codelistId: 'FILEFORMAT_VERSION',
            },
          ],
        },
      ],
    },
    {
      type: 'expand',
      role: 'basedOnIsbnForSchoolBooks',
      when: [
        {
          condition: [
            // Work litterature type is Lærebøker (høyere utd.)
            {
              regex: CodeListRef.LITERATURE_TYPE['Lærebøker (høyere utd.)'],
              arg: {$ref: '^work.literatureType'},
              default: false,
            },
            // and manifestation product form is Digitalt format, nedlastbart (download)
            {
              regex:
                CodeListRef.PRODUCT_FORM[
                  'Digitalt format, nedlastbart (download)'
                ],
              arg: {$ref: '/productForm'},
              default: false,
            },
          ],
          parts: [
            {
              type: 'codelist',
              name: 'basedOnIsbn',
              codelistId: 'expression_manifestation_based_on',
              hideIfNoOptionsAndEmpty: true,
            },
          ],
        },
      ],
    },
    {
      type: 'expand',
      role: 'digitalVersionIsbnForSchoolBooks',
      when: [
        {
          condition: [
            // Work litterature type is Lærebøker (høyere utd.)
            {
              regex: CodeListRef.LITERATURE_TYPE['Lærebøker (høyere utd.)'],
              arg: {$ref: '^work.literatureType'},
              default: false,
            },
            isOppositeOfDigitalFormat,
          ],
          parts: [
            {
              type: 'codelist',
              name: 'digitalVersionIsbn',
              codelistId: 'expression_manifestation_digital_version',
              hideIfNoOptionsAndEmpty: true,
            },
          ],
        },
      ],
    },
    {
      type: 'codelist',
      name: 'productGroup',
      codelistId: 'PRODUCT_GROUP',
      showCode: true,
    },
    {
      type: 'codelist',
      name: 'bookGroup',
      codelistId: 'BOOK_GROUP',
      required: 'should',
      showCode: true,
      readonly: true,
    },
    Separator('line', 'line'),
    {
      type: 'expand',
      role: 'formatSpecificProps',
      when: [
        {
          condition: {
            arg: {$ref: '/productForm'},
            regex: matchCodesRegex(pagesColorAndScaleForms),
            default: false,
          },
          parts: [pageParts, [colourPart], [scalePart]],
        },
        {
          condition: {
            arg: {$ref: '/productForm'},
            regex: matchCodesRegex(colourForms),
            default: false,
          },
          parts: [colourPart],
        },
        {
          condition: {
            arg: {$ref: '/productForm'},
            regex: matchCodesRegex(scaleForms),
            default: false,
          },
          parts: [scalePart],
        },
        {
          condition: {
            arg: {$ref: '/productForm'},
            regex: matchCodesRegex(pagesForms),
            default: false,
          },
          parts: [pageParts],
        },
        {
          condition: {
            arg: {$ref: '/productForm'},
            regex: matchCodesRegex(runtimeForms),
            default: false,
          },
          parts: [runtimePart],
        },
        {
          condition: {
            arg: {$ref: '/productForm'},
            regex: matchCodesRegex(noForms),
            default: false,
          },
          parts: [],
        },
      ],
      default: [pageParts, [runtimePart, scalePart, colourPart]],
    },
    {
      type: 'expand',
      role: 'showQuantityIfMerch',
      when: [
        {
          condition: isMerch,
          parts: [
            {
              type: 'int',
              name: 'packQuantity',
              key: 'packQuantity',
            },
          ],
        },
      ],
    },
  ],
};

export const manifestationSchemaModifiers: TypedSchemaModifierMap<
  ManifestationV4,
  MetadataModifierKey
> = {
  [`${WorkMerchandise}.${StatusModifierKey.precat}`]: [
    {
      name: 'imprints',
      required: 'should',
    },
    {
      name: 'productForm',
      required: 'should',
    },
    {
      name: 'productGroup',
      required: true,
    },
    {
      name: 'packQuantity',
      required: true,
    },
  ],
  [`${WorkMerchandise}.${StatusModifierKey.cat}`]: [
    {
      name: 'packQuantity',
      required: true,
    },
    {
      name: 'basedOnIsbn',
      required: 'should',
    },
    {
      name: 'imprints',
      required: 'should',
      validation: [
        validateLinkedImprintPlaceEmpty,
        validateLinkedImprintPlacePlaceholderString,
      ],
    },
    {
      name: 'productForm',
      required: true,
    },
    {
      name: 'dimensions',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'weight',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'pageCount',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'runtimeSeconds',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'scale',
      required: {
        should: {
          $or: [isMapCB, isMapCC],
        },
      },
      partContext: 'expand.when',
    },
    {
      name: 'packQuantity',
      required: true,
    },
  ],
  [`${WorkBook}.${StatusModifierKey.precat}`]: [
    {
      name: 'imprints',
      required: 'should',
    },
    {
      name: 'productForm',
      required: 'should',
    },
    {
      name: 'productGroup',
      required: true,
      validation: validate_productGroup_child_only_whenIntellectualChild,
    },
    {
      name: 'productFormDetail',
      required: {
        should: {
          $or: [isLydfil, isEbook],
        },
      },
    },
  ],
  [`${WorkBook}.${StatusModifierKey.cat}`]: [
    {
      name: 'basedOnIsbn',
      required: 'should',
    },
    {
      name: 'imprints',
      required: true,
      validation: [
        validateLinkedImprintPlaceEmpty,
        validateLinkedImprintPlacePlaceholderString,
      ],
    },
    {
      name: 'productForm',
      required: true,
    },
    {
      name: 'dimensions',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'weight',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'pageCount',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'runtimeSeconds',
      required: 'should',
      partContext: 'expand.when',
    },
    {
      name: 'scale',
      required: {
        should: {
          $or: [isMapCB, isMapCC],
        },
      },
      partContext: 'expand.when',
    },
    {
      name: 'productFormDetail',
      required: {
        required: {
          $or: [isLydfil, isEbook],
        },
      },
    },
  ],
};

/**
 * Used by getManifestationBasedModifier, but defined here to keep it close to schema definition.
 *  If bookGroup already has a value in a saved state, it should not be editable.
 */
export const EditableBookGroupModifier: ValuePartModifier<ManifestationV4> = {
  name: 'bookGroup',
  readonly: false,
};
